import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import RichTextEditor from '../components/RichTextEditor';
import { useTheme } from '../contexts/ThemeContext';
import { ArrowLeft, Save, Send, Eye, FileText, DollarSign, Plus, Trash2, ChevronDown, ChevronRight, Target, Layers, Users, Mail, AlertCircle, Check, X, UserPlus, Copy, RefreshCw, Building2, Settings, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { sanitizeHTML } from '../lib/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = '/qsecdef-logo.png';

// Helper function to fix relative image URLs in HTML content
const fixImageUrls = (htmlContent) => {
  if (!htmlContent) return '';
  return htmlContent
    // Relative paths
    .replace(/src=["']\/api\/public\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`)
    .replace(/src=["']\/api\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`)
    // Preview domain URLs
    .replace(/src=["']https:\/\/[^"']*\.preview\.emergentagent\.com\/api\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`)
    .replace(/src=["']https:\/\/[^"']*\.preview\.emergentagent\.com\/api\/public\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`)
    // Legacy emergent.host domain URLs
    .replace(/src=["']https:\/\/[^"']*\.emergent\.host\/api\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`)
    .replace(/src=["']https:\/\/[^"']*\.emergent\.host\/api\/public\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`);
};

// Helper to strip HTML tags
const stripHtml = (html) => {
  if (!html) return '';
  return sanitizeHTML(html).replace(/<[^>]*>/g, '');
};

// Helper to format date
const formatPdfDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

// Helper to load image as base64
const loadImageAsBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null); // Don't reject, just return null
    img.src = url;
  });
};

// Parse HTML content into structured blocks for PDF rendering
const parseHtmlToBlocks = (html) => {
  const blocks = [];
  const tmp = document.createElement('div');
  tmp.innerHTML = sanitizeHTML(html || '');
  
  const processNode = (node, inheritedStyle = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent.trim();
      if (text) {
        blocks.push({ type: 'text', content: text, style: { ...inheritedStyle } });
      }
      return;
    }
    
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    
    const tagName = node.tagName.toLowerCase();
    const style = { ...inheritedStyle };
    
    // Check for text alignment
    const textAlign = node.style.textAlign || 
      (node.getAttribute('style') || '').match(/text-align:\s*(\w+)/)?.[1];
    if (textAlign) style.align = textAlign;
    
    switch (tagName) {
      case 'h1':
        blocks.push({ type: 'heading', level: 1, content: node.textContent.trim(), style });
        blocks.push({ type: 'spacing', size: 8 });
        break;
      case 'h2':
        blocks.push({ type: 'heading', level: 2, content: node.textContent.trim(), style });
        blocks.push({ type: 'spacing', size: 6 });
        break;
      case 'h3':
        blocks.push({ type: 'heading', level: 3, content: node.textContent.trim(), style });
        blocks.push({ type: 'spacing', size: 4 });
        break;
      case 'p':
        const pText = node.textContent.trim();
        if (pText) {
          blocks.push({ type: 'paragraph', content: pText, style });
          blocks.push({ type: 'spacing', size: 4 });
        } else {
          blocks.push({ type: 'spacing', size: 6 });
        }
        break;
      case 'br':
        blocks.push({ type: 'spacing', size: 3 });
        break;
      case 'ul':
      case 'ol':
        const isOrdered = tagName === 'ol';
        let itemIndex = 0;
        node.querySelectorAll(':scope > li').forEach(li => {
          itemIndex++;
          const bullet = isOrdered ? `${itemIndex}.` : '•';
          blocks.push({ type: 'listItem', content: li.textContent.trim(), bullet, style });
        });
        blocks.push({ type: 'spacing', size: 4 });
        break;
      case 'img':
        const src = node.getAttribute('src');
        if (src) {
          blocks.push({ type: 'image', src, style });
          blocks.push({ type: 'spacing', size: 4 });
        }
        break;
      case 'hr':
        blocks.push({ type: 'hr' });
        blocks.push({ type: 'spacing', size: 6 });
        break;
      case 'blockquote':
        blocks.push({ type: 'blockquote', content: node.textContent.trim(), style });
        blocks.push({ type: 'spacing', size: 4 });
        break;
      case 'table':
        const rows = [];
        node.querySelectorAll('tr').forEach(tr => {
          const cells = [];
          tr.querySelectorAll('th, td').forEach(cell => {
            cells.push({ content: cell.textContent.trim(), isHeader: cell.tagName === 'TH' });
          });
          if (cells.length > 0) rows.push(cells);
        });
        if (rows.length > 0) {
          blocks.push({ type: 'table', rows, style });
          blocks.push({ type: 'spacing', size: 6 });
        }
        break;
      case 'strong':
      case 'b':
        blocks.push({ type: 'text', content: node.textContent.trim(), style: { ...style, bold: true } });
        break;
      case 'em':
      case 'i':
        blocks.push({ type: 'text', content: node.textContent.trim(), style: { ...style, italic: true } });
        break;
      case 'div':
        node.childNodes.forEach(child => processNode(child, style));
        break;
      default:
        node.childNodes.forEach(child => processNode(child, style));
    }
  };
  
  tmp.childNodes.forEach(node => processNode(node));
  return blocks;
};

const billingLabels = {
  monthly: 'Monthly Subscription',
  annual: 'Annual Payment',
  one_time: 'One-time',
  consultancy: 'Consultancy'
};

const ProposalEditor = () => {
  const { proposalId } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [proposal, setProposal] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Sidebar tab state
  const [activeSidebarTab, setActiveSidebarTab] = useState('recipients');
  
  const [sendData, setSendData] = useState({
    recipient_email: '',
    cc_emails: '',
    subject: '',
    message: ''
  });
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  
  // Recipients state
  const [cardContacts, setCardContacts] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState(new Set());
  const [showAddRecipientModal, setShowAddRecipientModal] = useState(false);
  const [newRecipient, setNewRecipient] = useState({ name: '', email: '', role: 'other' });
  const [addingRecipient, setAddingRecipient] = useState(false);
  const [showAddEmailModal, setShowAddEmailModal] = useState(false);
  const [contactToAddEmail, setContactToAddEmail] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Predefined roles for contacts
  const contactRoles = [
    { value: 'main_contact', label: 'Main Contact' },
    { value: 'billing', label: 'Billing' },
    { value: 'cmo', label: 'CMO' },
    { value: 'cro', label: 'CRO' },
    { value: 'ceo', label: 'CEO' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    fetchData();
  }, [proposalId]);

  // PDF Download function
  const downloadPdf = async () => {
    if (!proposal) return;
    setGeneratingPdf(true);
    
    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = margin;
      let currentPage = 1;

      // Colors
      const darkGreen = [34, 197, 94];
      const darkText = [17, 17, 17];
      const grayText = [102, 102, 102];
      const lightGray = [245, 245, 245];

      const addPageBackground = () => {
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');
      };

      const addFooter = () => {
        doc.setFontSize(8);
        doc.setTextColor(...grayText);
        doc.text('QSECDEF - Membership Proposal', margin, pageHeight - 10);
        doc.text(`Page ${currentPage}`, pageWidth - margin - 15, pageHeight - 10);
      };

      const checkNewPage = (requiredSpace = 40) => {
        if (yPos > pageHeight - requiredSpace) {
          addFooter();
          doc.addPage();
          currentPage++;
          addPageBackground();
          yPos = margin;
          return true;
        }
        return false;
      };

      addPageBackground();

      // Try to load logo
      let logoBase64 = null;
      try {
        logoBase64 = await loadImageAsBase64(LOGO_URL);
      } catch (e) {
        console.log('Could not load logo');
      }

      // === HEADER SECTION ===
      if (logoBase64) {
        doc.addImage(logoBase64, 'PNG', margin, yPos, 30, 30);
        yPos += 38;
      } else {
        yPos += 5;
      }

      doc.setFontSize(24);
      doc.setTextColor(...darkText);
      doc.setFont('helvetica', 'bold');
      doc.text('Membership Proposal', margin, yPos);
      yPos += 12;

      doc.setFontSize(18);
      doc.setTextColor(...darkGreen);
      doc.text(proposal.client?.company_name || 'Client', margin, yPos);
      yPos += 10;

      doc.setFontSize(11);
      doc.setTextColor(...grayText);
      doc.setFont('helvetica', 'normal');
      const contactName = `${proposal.client?.main_contact_first_name || ''} ${proposal.client?.main_contact_last_name || ''}`.trim();
      doc.text(`Prepared for: ${contactName || 'Client'}`, margin, yPos);
      yPos += 6;
      doc.text(`Date: ${formatPdfDate(proposal.created_at)}`, margin, yPos);
      yPos += 8;

      doc.setDrawColor(...darkGreen);
      doc.setLineWidth(1);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 18;

      // Helper function to render a block to PDF
      const renderBlock = async (block) => {
        switch (block.type) {
          case 'heading':
            checkNewPage(20);
            const headingSizes = { 1: 16, 2: 14, 3: 12 };
            doc.setFontSize(headingSizes[block.level] || 12);
            doc.setTextColor(...darkText);
            doc.setFont('helvetica', 'bold');
            const headingLines = doc.splitTextToSize(block.content, contentWidth);
            for (const line of headingLines) {
              checkNewPage(10);
              if (block.style?.align === 'center') {
                doc.text(line, pageWidth / 2, yPos, { align: 'center' });
              } else if (block.style?.align === 'right') {
                doc.text(line, pageWidth - margin, yPos, { align: 'right' });
              } else {
                doc.text(line, margin, yPos);
              }
              yPos += headingSizes[block.level] * 0.5;
            }
            break;
            
          case 'paragraph':
          case 'text':
            doc.setFontSize(10);
            doc.setTextColor(...grayText);
            doc.setFont('helvetica', block.style?.bold ? 'bold' : block.style?.italic ? 'italic' : 'normal');
            const pLines = doc.splitTextToSize(block.content, contentWidth);
            for (const line of pLines) {
              checkNewPage(8);
              if (block.style?.align === 'center') {
                doc.text(line, pageWidth / 2, yPos, { align: 'center' });
              } else if (block.style?.align === 'right') {
                doc.text(line, pageWidth - margin, yPos, { align: 'right' });
              } else {
                doc.text(line, margin, yPos);
              }
              yPos += 5;
            }
            break;
            
          case 'listItem':
            checkNewPage(8);
            doc.setFontSize(10);
            doc.setTextColor(...grayText);
            doc.setFont('helvetica', 'normal');
            doc.text(block.bullet, margin + 2, yPos);
            const itemLines = doc.splitTextToSize(block.content, contentWidth - 12);
            for (let i = 0; i < itemLines.length; i++) {
              if (i > 0) checkNewPage(6);
              doc.text(itemLines[i], margin + 10, yPos);
              yPos += 5;
            }
            break;
            
          case 'image':
            try {
              let imgSrc = block.src;
              if (imgSrc.startsWith('/')) {
                imgSrc = `${window.location.origin}${imgSrc}`;
              }
              const imgData = await loadImageAsBase64(imgSrc);
              if (imgData) {
                const img = new Image();
                img.src = imgData;
                await new Promise(resolve => { img.onload = resolve; img.onerror = resolve; });
                
                const maxWidth = contentWidth;
                const maxHeight = 80;
                let imgWidth = img.width * 0.264583;
                let imgHeight = img.height * 0.264583;
                
                if (imgWidth > maxWidth) {
                  const scale = maxWidth / imgWidth;
                  imgWidth = maxWidth;
                  imgHeight = imgHeight * scale;
                }
                if (imgHeight > maxHeight) {
                  const scale = maxHeight / imgHeight;
                  imgHeight = maxHeight;
                  imgWidth = imgWidth * scale;
                }
                
                checkNewPage(imgHeight + 10);
                const imgX = margin + (contentWidth - imgWidth) / 2;
                doc.addImage(imgData, 'PNG', imgX, yPos, imgWidth, imgHeight);
                yPos += imgHeight + 4;
              }
            } catch (e) {
              console.log('Could not load image:', block.src);
            }
            break;
            
          case 'hr':
            checkNewPage(10);
            doc.setDrawColor(...lightGray);
            doc.setLineWidth(0.5);
            doc.line(margin, yPos, pageWidth - margin, yPos);
            yPos += 2;
            break;
            
          case 'blockquote':
            checkNewPage(15);
            doc.setFillColor(...lightGray);
            doc.setDrawColor(...darkGreen);
            const quoteLines = doc.splitTextToSize(block.content, contentWidth - 15);
            const quoteHeight = quoteLines.length * 5 + 6;
            doc.rect(margin, yPos - 3, contentWidth, quoteHeight, 'F');
            doc.setLineWidth(1);
            doc.line(margin, yPos - 3, margin, yPos - 3 + quoteHeight);
            doc.setFontSize(10);
            doc.setTextColor(...grayText);
            doc.setFont('helvetica', 'italic');
            for (const line of quoteLines) {
              doc.text(line, margin + 8, yPos);
              yPos += 5;
            }
            yPos += 3;
            break;
            
          case 'table':
            if (block.rows && block.rows.length > 0) {
              checkNewPage(30);
              const colCount = block.rows[0].length;
              const colWidth = contentWidth / colCount;
              const cellPadding = 2;
              
              for (const row of block.rows) {
                checkNewPage(12);
                
                row.forEach((cell, colIndex) => {
                  const cellX = margin + (colIndex * colWidth);
                  
                  if (cell.isHeader) {
                    doc.setFillColor(...lightGray);
                    doc.rect(cellX, yPos - 5, colWidth, 8, 'F');
                  }
                  
                  doc.setDrawColor(200, 200, 200);
                  doc.setLineWidth(0.2);
                  doc.rect(cellX, yPos - 5, colWidth, 8);
                  
                  doc.setFontSize(9);
                  doc.setTextColor(...(cell.isHeader ? darkText : grayText));
                  doc.setFont('helvetica', cell.isHeader ? 'bold' : 'normal');
                  const cellText = doc.splitTextToSize(cell.content, colWidth - cellPadding * 2);
                  doc.text(cellText[0] || '', cellX + cellPadding, yPos);
                });
                
                yPos += 8;
              }
              yPos += 4;
            }
            break;
            
          case 'spacing':
            yPos += block.size || 4;
            break;
          
          default:
            break;
        }
      };

      // === SECTIONS ===
      if (proposal.sections && proposal.sections.length > 0) {
        for (const section of proposal.sections) {
          checkNewPage(60);

          doc.setFillColor(...darkGreen);
          doc.rect(margin, yPos - 5, 3, 10, 'F');
          
          doc.setFontSize(14);
          doc.setTextColor(...darkText);
          doc.setFont('helvetica', 'bold');
          doc.text(section.title, margin + 8, yPos);
          yPos += 12;

          // Parse and render section content as structured blocks
          const blocks = parseHtmlToBlocks(section.content);
          
          for (const block of blocks) {
            await renderBlock(block);
          }
          
          yPos += 8;
        }
      }

      // === PRICING SECTION ===
      if (proposal.pricing) {
        checkNewPage(80);

        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 12;

        doc.setFontSize(16);
        doc.setTextColor(...darkText);
        doc.setFont('helvetica', 'bold');
        doc.text('Pricing Summary', margin, yPos);
        yPos += 14;

        if (proposal.pricing.pricing_options?.length > 0) {
          for (const option of proposal.pricing.pricing_options) {
            checkNewPage(70);

            doc.setFillColor(...lightGray);
            doc.rect(margin, yPos - 6, contentWidth, 12, 'F');
            doc.setFontSize(12);
            doc.setTextColor(...darkText);
            doc.setFont('helvetica', 'bold');
            doc.text(option.name, margin + 5, yPos);
            yPos += 14;

            doc.setFontSize(9);
            doc.setTextColor(...grayText);
            doc.setFont('helvetica', 'bold');
            doc.text('Item', margin, yPos);
            doc.text('Qty', margin + 95, yPos);
            doc.text('Price', margin + 115, yPos);
            doc.text('Total', margin + 145, yPos);
            yPos += 6;

            doc.setDrawColor(200, 200, 200);
            doc.line(margin, yPos, pageWidth - margin, yPos);
            yPos += 6;

            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...darkText);
            for (const item of option.line_items || []) {
              checkNewPage(15);
              const itemName = (item.product_name || '').substring(0, 45);
              doc.text(itemName, margin, yPos);
              doc.text(String(item.quantity || 1), margin + 95, yPos);
              doc.text(`£${(item.unit_price_gbp || 0).toLocaleString()}`, margin + 115, yPos);
              doc.text(`£${(item.total_gbp || 0).toLocaleString()}`, margin + 145, yPos);
              yPos += 7;
            }

            yPos += 5;
            doc.setDrawColor(200, 200, 200);
            doc.line(margin + 100, yPos - 3, pageWidth - margin, yPos - 3);
            doc.setFontSize(12);
            doc.setTextColor(...darkGreen);
            doc.setFont('helvetica', 'bold');
            doc.text(`Total: £${(option.total_gbp || 0).toLocaleString()}`, margin + 125, yPos + 3);
            yPos += 18;
          }
        } else if (proposal.pricing.line_items?.length > 0) {
          doc.setFontSize(9);
          doc.setTextColor(...grayText);
          doc.setFont('helvetica', 'bold');
          doc.text('Item', margin, yPos);
          doc.text('Qty', margin + 95, yPos);
          doc.text('Price', margin + 115, yPos);
          doc.text('Total', margin + 145, yPos);
          yPos += 6;

          doc.setDrawColor(200, 200, 200);
          doc.line(margin, yPos, pageWidth - margin, yPos);
          yPos += 6;

          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...darkText);
          for (const item of proposal.pricing.line_items) {
            checkNewPage(15);
            const itemName = (item.product_name || '').substring(0, 45);
            doc.text(itemName, margin, yPos);
            doc.text(String(item.quantity || 1), margin + 95, yPos);
            doc.text(`£${(item.unit_price_gbp || 0).toLocaleString()}`, margin + 115, yPos);
            doc.text(`£${(item.total_gbp || 0).toLocaleString()}`, margin + 145, yPos);
            yPos += 7;
          }

          yPos += 8;
          doc.setDrawColor(200, 200, 200);
          doc.line(margin + 100, yPos - 5, pageWidth - margin, yPos - 5);
          doc.setFontSize(10);
          doc.setTextColor(...darkText);
          doc.text(`Subtotal: £${(proposal.pricing.subtotal_gbp || 0).toLocaleString()}`, margin + 115, yPos);
          yPos += 6;
          if (proposal.pricing.charge_vat && proposal.pricing.tax_amount_gbp > 0) {
            doc.text(`VAT: £${(proposal.pricing.tax_amount_gbp || 0).toLocaleString()}`, margin + 115, yPos);
            yPos += 8;
          }
          doc.setFontSize(14);
          doc.setTextColor(...darkGreen);
          doc.setFont('helvetica', 'bold');
          doc.text(`Total: £${(proposal.pricing.total_gbp || 0).toLocaleString()}`, margin + 115, yPos);
        }
      }

      // === SIGNATURE SECTION (if signed) ===
      const sig = proposal.signature || proposal.signatures?.[0];
      
      if (proposal.status === 'signed' && sig) {
        checkNewPage(80);

        yPos += 12;
        doc.setDrawColor(...darkGreen);
        doc.setLineWidth(1);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 12;

        // Green "SIGNED" badge
        doc.setFillColor(34, 197, 94);
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.roundedRect(margin, yPos - 6, 50, 10, 2, 2, 'F');
        doc.text('SIGNED', margin + 5, yPos);
        yPos += 15;

        doc.setFontSize(10);
        doc.setTextColor(...darkText);
        doc.setFont('helvetica', 'normal');
        doc.text(`Signed by: ${sig.signer_name || ''}`, margin, yPos);
        yPos += 6;
        if (sig.signer_title) {
          doc.text(`Title: ${sig.signer_title}`, margin, yPos);
          yPos += 6;
        }
        if (sig.signer_email) {
          doc.text(`Email: ${sig.signer_email}`, margin, yPos);
          yPos += 6;
        }
        doc.text(`Date: ${formatPdfDate(sig.signed_at)}`, margin, yPos);
        yPos += 10;
        
        // Add signature image
        if (sig.signature_data && sig.signature_data.startsWith('data:image')) {
          // Draw signature box
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.5);
          doc.rect(margin, yPos, 80, 35);
          
          doc.setFontSize(8);
          doc.setTextColor(...grayText);
          doc.text('Signature:', margin + 2, yPos + 5);
          
          try {
            // Add the actual signature image
            doc.addImage(sig.signature_data, 'PNG', margin + 5, yPos + 8, 70, 22);
          } catch (e) {
            console.log('Could not add signature image:', e);
            // Fallback text if image fails
            doc.setFontSize(10);
            doc.setTextColor(...darkText);
            doc.text('[Digital Signature on File]', margin + 10, yPos + 20);
          }
          yPos += 40;
        }
        
        // Legal statement
        yPos += 5;
        doc.setFontSize(8);
        doc.setTextColor(...grayText);
        const legalText = `This document was electronically signed on ${formatPdfDate(sig.signed_at)}. The signer confirmed their agreement to the terms and conditions of this proposal.`;
        const legalLines = doc.splitTextToSize(legalText, contentWidth);
        for (const line of legalLines) {
          doc.text(line, margin, yPos);
          yPos += 4;
        }
      }

      addFooter();
      doc.save(`proposal-${proposal.client?.company_name || 'document'}.pdf`);

    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const fetchData = async () => {
    try {
      const [proposalRes, productsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/proposals/${proposalId}`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/products`, { credentials: 'include' })
      ]);
      
      if (proposalRes.ok) {
        const propData = await proposalRes.json();
        // Fix image URLs in all sections
        if (propData.sections) {
          propData.sections = propData.sections.map(section => ({
            ...section,
            content: fixImageUrls(section.content)
          }));
        }
        setProposal(propData);
        setActiveSection(propData.sections?.[0]?.section_id || null);
        
        // Initialize selected recipients from existing proposal recipients
        if (propData.recipients?.length > 0) {
          const activeRecipients = propData.recipients
            .filter(r => r.is_active && r.email && r.email.trim())
            .map(r => r.email.toLowerCase());
          setSelectedRecipients(new Set(activeRecipients));
        }
        
        // Fetch card contacts
        if (propData.card_id) {
          const cardRes = await fetch(`${BACKEND_URL}/api/cards/${propData.card_id}`, { credentials: 'include' });
          if (cardRes.ok) {
            const cardData = await cardRes.json();
            setCardContacts(cardData.key_people || []);
          }
        }
        
        // Pre-fill send data
        setSendData({
          recipient_email: propData.client?.main_contact_email || '',
          cc_emails: propData.client?.billing_contact_email || '',
          subject: `Your QSECDEF Membership Proposal - ${propData.client?.company_name || ''}`,
          message: `Dear ${propData.client?.main_contact_first_name || 'there'},\n\nPlease find your personalized membership proposal attached.\n\nIf you have any questions, please don't hesitate to reach out.\n\nBest regards,\nThe QSECDEF Team`
        });
      }
      
      if (productsRes.ok) {
        setProducts(await productsRes.json());
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveProposal = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/proposals/${proposalId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: proposal.title,
          sections: proposal.sections,
          pricing: proposal.pricing
        })
      });
      
      if (response.ok) {
        alert('Proposal saved!');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save');
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save proposal');
    } finally {
      setSaving(false);
    }
  };

  const sendProposal = async () => {
    try {
      // First save
      await saveProposal();
      
      // Get the first selected recipient's email (or use sendData if no selection)
      const recipientEmails = Array.from(selectedRecipients);
      const primaryRecipient = recipientEmails[0] || sendData.recipient_email;
      
      if (!primaryRecipient) {
        alert('Please select at least one recipient');
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/proposals/${proposalId}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          recipient_email: primaryRecipient,
          cc_emails: recipientEmails.slice(1), // Additional recipients as CC
          subject: sendData.subject,
          message: sendData.message
        })
      });
      
      if (response.ok) {
        alert('Proposal sent successfully!');
        navigate('/proposals');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to send');
      }
    } catch (error) {
      console.error('Send error:', error);
      alert('Failed to send proposal');
    }
  };

  const updateSection = (sectionId, content) => {
    setProposal({
      ...proposal,
      sections: proposal.sections.map(s =>
        s.section_id === sectionId ? { ...s, content } : s
      )
    });
  };

  // Calculate pricing option totals
  const calculateOptionTotals = (lineItems, chargeVat = false, vatRate = 0.20) => {
    const subtotal = lineItems.reduce((sum, item) => sum + item.total_gbp, 0);
    const taxAmount = chargeVat ? subtotal * vatRate : 0;
    const total = subtotal + taxAmount;
    
    // Calculate MRR/ARR - always use NET (pre-tax) amounts
    let mrr = 0;
    let arr = 0;
    lineItems.forEach(item => {
      if (item.billing_frequency === 'monthly') {
        mrr += item.total_gbp;  // Net amount
        arr += item.total_gbp * 12;
      } else if (item.billing_frequency === 'annual') {
        mrr += item.total_gbp / 12;
        arr += item.total_gbp;  // Net amount
      }
    });
    
    return { subtotal, taxRate: vatRate, taxAmount, total, mrr, arr, chargeVat };
  };

  // Add product with variant selection - creates pricing options
  const addProductWithVariants = (product, selectedVariants) => {
    const pricingOptions = proposal.pricing?.pricing_options || [];
    const chargeVat = product.charge_vat || false;
    const vatRate = product.vat_rate || 0.20;
    
    selectedVariants.forEach(variant => {
      // Find or create the pricing option for this billing frequency
      let optionId = `opt_${variant.billing_frequency}`;
      let existingOption = pricingOptions.find(o => o.option_id === optionId);
      
      const lineItem = {
        product_id: product.product_id,
        product_name: product.name,
        description: product.description,
        quantity: 1,
        unit_price_gbp: variant.price_gbp,
        billing_frequency: variant.billing_frequency,
        total_gbp: variant.price_gbp
      };
      
      if (existingOption) {
        // Add to existing option
        existingOption.line_items.push(lineItem);
        const totals = calculateOptionTotals(existingOption.line_items, chargeVat, vatRate);
        existingOption.subtotal_gbp = totals.subtotal;
        existingOption.charge_vat = chargeVat;
        existingOption.tax_rate = vatRate;
        existingOption.tax_amount_gbp = totals.taxAmount;
        existingOption.total_gbp = totals.total;
        existingOption.mrr_value = totals.mrr;
        existingOption.arr_value = totals.arr;
      } else {
        // Create new option
        const totals = calculateOptionTotals([lineItem], chargeVat, vatRate);
        pricingOptions.push({
          option_id: optionId,
          name: billingLabels[variant.billing_frequency] || variant.billing_frequency,
          billing_frequency: variant.billing_frequency,
          line_items: [lineItem],
          subtotal_gbp: totals.subtotal,
          charge_vat: chargeVat,
          tax_rate: vatRate,
          tax_amount_gbp: totals.taxAmount,
          total_gbp: totals.total,
          mrr_value: totals.mrr,
          arr_value: totals.arr
        });
      }
    });
    
    // Sort options: monthly first, then annual
    pricingOptions.sort((a, b) => {
      const order = { monthly: 0, annual: 1, one_time: 2, consultancy: 3 };
      return (order[a.billing_frequency] || 99) - (order[b.billing_frequency] || 99);
    });
    
    setProposal({
      ...proposal,
      pricing: {
        ...proposal.pricing,
        pricing_options: pricingOptions
      }
    });
    
    setShowAddProductModal(false);
    setSelectedProduct(null);
  };

  // Legacy: Add single product (for products without variants)
  const addLineItem = (product) => {
    const lineItem = {
      product_id: product.product_id,
      product_name: product.name,
      description: product.description,
      quantity: 1,
      unit_price_gbp: product.price_gbp,
      billing_frequency: product.billing_frequency,
      total_gbp: product.price_gbp
    };
    
    const newLineItems = [...(proposal.pricing?.line_items || []), lineItem];
    updateLegacyPricing(newLineItems);
  };

  const removeLineItemFromOption = (optionId, itemIndex) => {
    const pricingOptions = proposal.pricing?.pricing_options?.map(opt => {
      if (opt.option_id === optionId) {
        const newLineItems = opt.line_items.filter((_, i) => i !== itemIndex);
        if (newLineItems.length === 0) return null; // Remove empty options
        const totals = calculateOptionTotals(newLineItems);
        return {
          ...opt,
          line_items: newLineItems,
          subtotal_gbp: totals.subtotal,
          tax_amount_gbp: totals.taxAmount,
          total_gbp: totals.total,
          mrr_value: totals.mrr,
          arr_value: totals.arr
        };
      }
      return opt;
    }).filter(Boolean) || [];
    
    setProposal({
      ...proposal,
      pricing: {
        ...proposal.pricing,
        pricing_options: pricingOptions
      }
    });
  };

  const updateQuantityInOption = (optionId, itemIndex, quantity) => {
    const pricingOptions = proposal.pricing?.pricing_options?.map(opt => {
      if (opt.option_id === optionId) {
        const newLineItems = opt.line_items.map((item, i) => {
          if (i === itemIndex) {
            return { ...item, quantity, total_gbp: item.unit_price_gbp * quantity };
          }
          return item;
        });
        const totals = calculateOptionTotals(newLineItems);
        return {
          ...opt,
          line_items: newLineItems,
          subtotal_gbp: totals.subtotal,
          tax_amount_gbp: totals.taxAmount,
          total_gbp: totals.total,
          mrr_value: totals.mrr,
          arr_value: totals.arr
        };
      }
      return opt;
    }) || [];
    
    setProposal({
      ...proposal,
      pricing: {
        ...proposal.pricing,
        pricing_options: pricingOptions
      }
    });
  };

  // Update price in a pricing option
  const updatePriceInOption = (optionId, itemIndex, newPrice) => {
    const pricingOptions = proposal.pricing?.pricing_options?.map(opt => {
      if (opt.option_id === optionId) {
        const newLineItems = opt.line_items.map((item, i) => {
          if (i === itemIndex) {
            return { ...item, unit_price_gbp: newPrice, total_gbp: newPrice * item.quantity };
          }
          return item;
        });
        const totals = calculateOptionTotals(newLineItems);
        return {
          ...opt,
          line_items: newLineItems,
          subtotal_gbp: totals.subtotal,
          tax_amount_gbp: totals.taxAmount,
          total_gbp: totals.total,
          mrr_value: totals.mrr,
          arr_value: totals.arr
        };
      }
      return opt;
    }) || [];
    
    setProposal({
      ...proposal,
      pricing: {
        ...proposal.pricing,
        pricing_options: pricingOptions
      }
    });
  };

  // Legacy pricing update
  const updateLegacyPricing = (lineItems) => {
    const subtotal = lineItems.reduce((sum, item) => sum + item.total_gbp, 0);
    const taxRate = 0.20;
    const taxAmount = subtotal * taxRate;
    const total = subtotal + taxAmount;
    
    const hasMembership = lineItems.find(item => 
      item.billing_frequency === 'monthly' || item.billing_frequency === 'annual'
    );
    
    setProposal({
      ...proposal,
      pricing: {
        ...proposal.pricing,
        line_items: lineItems,
        subtotal_gbp: subtotal,
        tax_rate: taxRate,
        tax_amount_gbp: taxAmount,
        total_gbp: total,
        billing_type: hasMembership?.billing_frequency || null
      }
    });
  };

  const removeLineItem = (index) => {
    const newLineItems = proposal.pricing.line_items.filter((_, i) => i !== index);
    updateLegacyPricing(newLineItems);
  };

  const updateLineItemQuantity = (index, quantity) => {
    const newLineItems = proposal.pricing.line_items.map((item, i) => {
      if (i === index) {
        return { ...item, quantity, total_gbp: item.unit_price_gbp * quantity };
      }
      return item;
    });
    updateLegacyPricing(newLineItems);
  };

  const updateLineItemPrice = (index, newPrice) => {
    const newLineItems = proposal.pricing.line_items.map((item, i) => {
      if (i === index) {
        return { ...item, unit_price_gbp: newPrice, total_gbp: newPrice * item.quantity };
      }
      return item;
    });
    updateLegacyPricing(newLineItems);
  };

  // Handle product selection
  const handleProductSelect = (productId) => {
    const product = products.find(p => p.product_id === productId);
    if (!product) return;
    
    // If product has variants, show modal for selection
    if (product.variants && product.variants.length > 0) {
      setSelectedProduct(product);
      setShowAddProductModal(true);
    } else {
      // Legacy: just add the product directly
      addLineItem(product);
    }
  };

  // ============ RECIPIENT FUNCTIONS ============
  
  // Toggle recipient selection
  const toggleRecipient = async (email) => {
    const newSelected = new Set(selectedRecipients);
    
    if (newSelected.has(email.toLowerCase())) {
      // Remove recipient
      newSelected.delete(email.toLowerCase());
      
      // Find and deactivate in backend
      const recipient = proposal.recipients?.find(r => r.email.toLowerCase() === email.toLowerCase());
      if (recipient) {
        try {
          await fetch(`${BACKEND_URL}/api/proposals/${proposalId}/recipients/${recipient.recipient_id}`, {
            method: 'DELETE',
            credentials: 'include'
          });
        } catch (error) {
          console.error('Failed to deactivate recipient:', error);
        }
      }
    } else {
      // Add recipient
      newSelected.add(email.toLowerCase());
      
      // Check if already exists in proposal
      const existingRecipient = proposal.recipients?.find(r => r.email.toLowerCase() === email.toLowerCase());
      
      if (existingRecipient && !existingRecipient.is_active) {
        // Reactivate
        try {
          await fetch(`${BACKEND_URL}/api/proposals/${proposalId}/recipients/${existingRecipient.recipient_id}/reactivate`, {
            method: 'POST',
            credentials: 'include'
          });
        } catch (error) {
          console.error('Failed to reactivate recipient:', error);
        }
      } else if (!existingRecipient) {
        // Add new recipient
        const contact = cardContacts.find(c => c.email?.toLowerCase() === email.toLowerCase());
        if (contact) {
          try {
            await fetch(`${BACKEND_URL}/api/proposals/${proposalId}/recipients`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Contact',
                email: contact.email,
                role: contact.role || 'other',
                add_to_card: false
              })
            });
          } catch (error) {
            console.error('Failed to add recipient:', error);
          }
        }
      }
    }
    
    setSelectedRecipients(newSelected);
    // Refresh proposal data
    fetchData();
  };

  // Add new recipient (creates contact on card and adds to proposal)
  const addNewRecipient = async () => {
    if (!newRecipient.name || !newRecipient.email) {
      alert('Please enter name and email');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newRecipient.email)) {
      alert('Please enter a valid email address');
      return;
    }
    
    setAddingRecipient(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/proposals/${proposalId}/recipients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newRecipient.name,
          email: newRecipient.email,
          role: newRecipient.role,
          add_to_card: true
        })
      });
      
      if (response.ok) {
        setShowAddRecipientModal(false);
        setNewRecipient({ name: '', email: '', role: 'other' });
        setSelectedRecipients(prev => new Set([...prev, newRecipient.email.toLowerCase()]));
        fetchData(); // Refresh to get updated card contacts and proposal
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to add recipient');
      }
    } catch (error) {
      console.error('Add recipient error:', error);
      alert('Failed to add recipient');
    } finally {
      setAddingRecipient(false);
    }
  };

  // Add email to existing contact
  const addEmailToContact = async () => {
    if (!contactToAddEmail || !newEmail) return;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      alert('Please enter a valid email address');
      return;
    }
    
    try {
      // Update the card's key_people
      const cardRes = await fetch(`${BACKEND_URL}/api/cards/${proposal.card_id}`, { credentials: 'include' });
      if (cardRes.ok) {
        const cardData = await cardRes.json();
        const updatedKeyPeople = cardData.key_people.map(kp => {
          if (kp.role === contactToAddEmail.role && 
              kp.first_name === contactToAddEmail.first_name && 
              kp.last_name === contactToAddEmail.last_name) {
            return { ...kp, email: newEmail };
          }
          return kp;
        });
        
        await fetch(`${BACKEND_URL}/api/cards/${proposal.card_id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ key_people: updatedKeyPeople })
        });
        
        setShowAddEmailModal(false);
        setContactToAddEmail(null);
        setNewEmail('');
        fetchData(); // Refresh contacts
      }
    } catch (error) {
      console.error('Add email error:', error);
      alert('Failed to add email');
    }
  };

  // Get recipient link
  const getRecipientLink = (recipient) => {
    return `${window.location.origin}/p/${proposal.access_token}/r/${recipient.access_token}`;
  };

  // Copy recipient link
  const copyRecipientLink = (recipient) => {
    const link = getRecipientLink(recipient);
    navigator.clipboard.writeText(link);
    alert('Link copied!');
  };

  // Helper to strip HTML tags for plain text message
  const stripHtmlForMessage = (html) => {
    if (!html) return '';
    // Replace common HTML tags with appropriate text
    let text = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<p[^>]*>/gi, '')
      .replace(/<\/div>/gi, '\n')
      .replace(/<div[^>]*>/gi, '')
      .replace(/<li[^>]*>/gi, '• ')
      .replace(/<\/li>/gi, '\n')
      .replace(/<[^>]+>/g, '');
    // Decode HTML entities
    const textarea = document.createElement('textarea');
    textarea.innerHTML = sanitizeHTML(text);
    text = textarea.value;
    // Clean up excessive newlines
    return text.replace(/\n{3,}/g, '\n\n').trim();
  };

  // Open send modal and fetch email template
  const openSendModal = async () => {
    setShowSendModal(true);
    setLoadingTemplate(true);
    
    try {
      // Fetch the new_proposal email template
      const templatesRes = await fetch(`${BACKEND_URL}/api/system-emails/templates?category=proposals`, { 
        credentials: 'include' 
      });
      
      if (templatesRes.ok) {
        const templates = await templatesRes.json();
        const proposalTemplate = templates.find(t => t.template_key === 'new_proposal');
        
        if (proposalTemplate) {
          // Replace template variables with actual values
          let messageBody = proposalTemplate.body;
          const recipientFirstName = proposal.client?.main_contact_first_name || 'there';
          const companyName = proposal.client?.company_name || 'your company';
          const proposalValue = proposal.pricing?.total_gbp 
            ? `£${proposal.pricing.total_gbp.toLocaleString()}`
            : 'as outlined';
          
          // Replace common variables
          messageBody = messageBody
            .replace(/\{\{recipient_first_name\}\}/g, recipientFirstName)
            .replace(/\{\{recipient_name\}\}/g, `${proposal.client?.main_contact_first_name || ''} ${proposal.client?.main_contact_last_name || ''}`.trim() || 'Valued Customer')
            .replace(/\{\{company_name\}\}/g, companyName)
            .replace(/\{\{proposal_link\}\}/g, '[Link will be included in email]')
            .replace(/\{\{proposal_value\}\}/g, proposalValue)
            .replace(/\{\{expiry_date\}\}/g, 'within 30 days')
            .replace(/\{\{sender_name\}\}/g, '')
            .replace(/\{\{sender_first_name\}\}/g, '')
            .replace(/\{\{sender_title\}\}/g, '');
          
          // Convert HTML to plain text for textarea
          const plainTextMessage = stripHtmlForMessage(messageBody);
          
          // Also update subject if available
          let subject = proposalTemplate.subject;
          subject = subject
            .replace(/\{\{company_name\}\}/g, companyName)
            .replace(/\{\{recipient_first_name\}\}/g, recipientFirstName);
          
          setSendData(prev => ({
            ...prev,
            subject: subject,
            message: plainTextMessage
          }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch email template:', error);
      // Keep existing default message if fetch fails
    } finally {
      setLoadingTemplate(false);
    }
  };

  if (loading) {
    return (
      <AppLayout title="Edit Proposal">
        <div className="flex items-center justify-center h-64">
          <div className="text-[#A2FF00] text-xl">Loading...</div>
        </div>
      </AppLayout>
    );
  }

  if (!proposal) {
    return (
      <AppLayout title="Edit Proposal">
        <div className="text-center py-12">
          <h2 className="text-xl text-white mb-4">Proposal not found</h2>
          <button onClick={() => navigate('/proposals')} className="text-[#A2FF00] hover:underline">
            Back to Proposals
          </button>
        </div>
      </AppLayout>
    );
  }

  const activeContent = proposal.sections?.find(s => s.section_id === activeSection);
  const hasPricingOptions = proposal.pricing?.pricing_options?.length > 0;

  return (
    <AppLayout title="Edit Proposal">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/proposals')}
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                {proposal.title}
              </h1>
              <span className={`text-xs px-2 py-0.5 rounded ${
                proposal.status === 'draft' ? 'bg-gray-500/20 text-gray-400' :
                proposal.status === 'sent' ? 'bg-blue-500/20 text-blue-400' :
                proposal.status === 'viewed' ? 'bg-yellow-500/20 text-yellow-400' :
                proposal.status === 'signed' ? 'bg-green-500/20 text-green-400' :
                proposal.status === 'declined' ? 'bg-red-500/20 text-red-400' :
                'bg-gray-500/20 text-gray-400'
              }`}>
                {proposal.status?.toUpperCase()}
              </span>
            </div>
            
            {/* Decline Info Banner */}
            {proposal.status === 'declined' && proposal.decline_info && (
              <div className="mt-2 p-3 bg-red-500/10 border border-red-500/30 rounded-sm">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-red-400 font-medium">
                      Declined by {proposal.decline_info.decliner_name}
                    </p>
                    <p className="text-gray-400 text-xs mt-1">
                      Reason: {
                        proposal.decline_info.reason === 'budget' ? 'Budget constraints' :
                        proposal.decline_info.reason === 'timing' ? 'Timing not right' :
                        proposal.decline_info.reason === 'competitor' ? 'Chose competitor' :
                        proposal.decline_info.reason === 'not_needed' ? 'No longer needed' :
                        proposal.decline_info.reason === 'other' ? 'Other' :
                        proposal.decline_info.reason
                      }
                    </p>
                    {proposal.decline_info.feedback && (
                      <p className="text-gray-300 text-xs mt-2 italic">
                        &ldquo;{proposal.decline_info.feedback}&rdquo;
                      </p>
                    )}
                    <p className="text-gray-500 text-xs mt-2">
                      {new Date(proposal.decline_info.declined_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {proposal.access_token && (
              <a
                href={`/p/${proposal.access_token}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-sm transition-colors"
              >
                <Eye className="w-4 h-4" />
                Preview
              </a>
            )}
            <button
              onClick={downloadPdf}
              disabled={generatingPdf}
              className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-sm transition-colors disabled:opacity-50"
            >
              <Download className={`w-4 h-4 ${generatingPdf ? 'animate-pulse' : ''}`} />
              {generatingPdf ? 'Generating...' : 'PDF'}
            </button>
            {/* Save Button - Always visible unless signed or expired */}
            {proposal.status !== 'signed' && proposal.status !== 'expired' && (
              <button
                onClick={saveProposal}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2.5 bg-[#A2FF00] hover:bg-[#8EFF00] text-black text-base font-bold rounded-sm transition-colors disabled:opacity-50 shadow-lg"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            )}
            {proposal.status === 'draft' && (
              <button
                onClick={openSendModal}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-sm transition-colors"
              >
                <Send className="w-4 h-4" />
                Send
              </button>
            )}
            {(proposal.status === 'sent' || proposal.status === 'viewed') && (
              <button
                onClick={openSendModal}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-sm transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Resend
              </button>
            )}
            {proposal.status === 'signed' && (
              <span className="text-xs text-green-400 bg-green-500/10 px-3 py-2 rounded-sm">
                ✓ Signed - Read only
              </span>
            )}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-4 gap-4">
          {/* Sections List */}
          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4`}>
            <h3 className={`text-sm font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} mb-3 flex items-center gap-2`}>
              <FileText className="w-4 h-4" />
              SECTIONS
            </h3>
            <div className="space-y-1">
              {proposal.sections?.map((section, index) => (
                <button
                  key={section.section_id}
                  onClick={() => setActiveSection(section.section_id)}
                  className={`w-full text-left px-3 py-2 rounded-sm text-sm flex items-center gap-2 ${
                    activeSection === section.section_id
                      ? isDark ? 'bg-[#A2FF00]/20 text-[#A2FF00]' : 'bg-green-100 text-green-700'
                      : isDark ? 'text-gray-400 hover:bg-white/5' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <ChevronRight className={`w-3 h-3 transition-transform ${activeSection === section.section_id ? 'rotate-90' : ''}`} />
                  {section.title || `Section ${index + 1}`}
                </button>
              ))}
            </div>
          </div>

          {/* Editor */}
          <div className={`lg:col-span-2 ${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm`}>
            {activeContent ? (
              <div className="p-4">
                <h3 className={`${isDark ? 'text-white' : 'text-gray-900'} font-bold mb-4`}>{activeContent.title}</h3>
                {proposal.status === 'signed' ? (
                  <div 
                    className={`prose ${isDark ? 'prose-invert' : ''} max-w-none ${isDark ? 'prose-p:text-gray-300' : 'prose-p:text-gray-600'}`}
                    dangerouslySetInnerHTML={{ __html: sanitizeHTML(activeContent.content || '<p>No content</p>') }}
                  />
                ) : (
                  <RichTextEditor
                    key={activeSection}
                    editorKey={activeSection}
                    content={activeContent.content || ''}
                    onChange={(content) => updateSection(activeSection, content)}
                  />
                )}
              </div>
            ) : (
              <div className="p-4 text-center text-gray-500">
                Select a section to edit
              </div>
            )}
          </div>

          {/* Right Sidebar - Tabbed */}
          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm overflow-hidden`}>
            {/* Tab Navigation */}
            <div className={`flex border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <button
                onClick={() => setActiveSidebarTab('recipients')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors ${
                  activeSidebarTab === 'recipients'
                    ? isDark ? 'bg-[#A2FF00]/10 text-[#A2FF00] border-b-2 border-[#A2FF00]' : 'bg-green-50 text-green-600 border-b-2 border-green-500'
                    : isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Users className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Recipients</span>
                {selectedRecipients.size > 0 && (
                  <span className={`${isDark ? 'bg-[#A2FF00]/20 text-[#A2FF00]' : 'bg-green-100 text-green-600'} text-[10px] px-1.5 py-0.5 rounded-full`}>
                    {selectedRecipients.size}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveSidebarTab('pricing')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors ${
                  activeSidebarTab === 'pricing'
                    ? isDark ? 'bg-[#A2FF00]/10 text-[#A2FF00] border-b-2 border-[#A2FF00]' : 'bg-green-50 text-green-600 border-b-2 border-green-500'
                    : isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <DollarSign className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Pricing</span>
                {(proposal.pricing?.pricing_options?.length > 0 || proposal.pricing?.line_items?.length > 0) && (
                  <span className={`${isDark ? 'bg-[#A2FF00]/20 text-[#A2FF00]' : 'bg-green-100 text-green-600'} text-[10px] px-1.5 py-0.5 rounded-full`}>
                    {proposal.pricing?.pricing_options?.length || proposal.pricing?.line_items?.length || 0}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveSidebarTab('client')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors ${
                  activeSidebarTab === 'client'
                    ? isDark ? 'bg-[#A2FF00]/10 text-[#A2FF00] border-b-2 border-[#A2FF00]' : 'bg-green-50 text-green-600 border-b-2 border-green-500'
                    : isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Building2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Client</span>
              </button>
              <button
                onClick={() => setActiveSidebarTab('settings')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors ${
                  activeSidebarTab === 'settings'
                    ? 'bg-[#A2FF00]/10 text-[#A2FF00] border-b-2 border-[#A2FF00]'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Settings className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Settings</span>
              </button>
            </div>
            
            {/* Tab Content */}
            <div className="p-4 max-h-[calc(100vh-280px)] overflow-y-auto">
              
              {/* PRICING TAB */}
              {activeSidebarTab === 'pricing' && (
                <div>
                  {/* Pricing Options Display */}
                  {hasPricingOptions ? (
                    <div className="space-y-4">
                      <div className="text-xs text-gray-400 bg-blue-500/10 border border-blue-500/20 rounded p-2">
                        <Layers className="w-3 h-3 inline mr-1" />
                        Customer will choose between these options
                      </div>
                      
                      {proposal.pricing.pricing_options.map((option, optIdx) => (
                        <div key={option.option_id} className={`border ${isDark ? 'border-white/10' : 'border-gray-200'} rounded-sm overflow-hidden`}>
                          <div className={`${isDark ? 'bg-white/5' : 'bg-gray-50'} px-3 py-2 flex items-center justify-between`}>
                            <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{option.name}</span>
                            <span className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'} font-bold`}>£{option.total_gbp.toLocaleString()}</span>
                          </div>
                          
                          <div className="p-2 space-y-2">
                            {option.line_items.map((item, itemIdx) => (
                              <div key={itemIdx} className={`${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-100'} rounded p-2`}>
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className={`${isDark ? 'text-white' : 'text-gray-900'} text-xs font-medium truncate`}>{item.product_name}</div>
                                    <div className="text-gray-500 text-xs">/{option.billing_frequency}</div>
                                  </div>
                                  <button
                                    onClick={() => removeLineItemFromOption(option.option_id, itemIdx)}
                                    className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                                <div className="flex items-center justify-between mt-2 gap-2">
                                  <div className="flex items-center gap-2">
                                    <label className="text-xs text-gray-500">Qty:</label>
                                    <input
                                      type="number"
                                      min="1"
                                      value={item.quantity}
                                      onChange={(e) => updateQuantityInOption(option.option_id, itemIdx, parseInt(e.target.value) || 1)}
                                      className={`w-14 ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded px-2 py-1 text-xs`}
                                    />
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <label className="text-xs text-gray-500">£</label>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={item.unit_price_gbp}
                                      onChange={(e) => updatePriceInOption(option.option_id, itemIdx, parseFloat(e.target.value) || 0)}
                                      className={`w-20 ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded px-2 py-1 text-xs text-right`}
                                    />
                                  </div>
                                  <span className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'} text-sm font-medium min-w-[70px] text-right`}>
                                    £{item.total_gbp.toLocaleString()}
                                  </span>
                                </div>
                              </div>
                            ))}
                            
                            <div className={`pt-2 mt-2 border-t ${isDark ? 'border-white/10' : 'border-gray-200'} space-y-1 text-xs`}>
                              <div className="flex justify-between text-gray-400">
                                <span>Subtotal</span>
                                <span>£{option.subtotal_gbp.toLocaleString()}</span>
                              </div>
                              {option.charge_vat && option.tax_amount_gbp > 0 && (
                                <div className="flex justify-between text-gray-400">
                                  <span>VAT ({Math.round((option.tax_rate || 0.20) * 100)}%)</span>
                                  <span>£{option.tax_amount_gbp.toLocaleString()}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Legacy Line Items */
                    <div className="space-y-2">
                      {proposal.pricing?.line_items?.map((item, index) => (
                        <div key={index} className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-100 border-gray-200'} border rounded-sm p-2`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className={`${isDark ? 'text-white' : 'text-gray-900'} text-sm font-medium truncate`}>{item.product_name}</div>
                              <div className="text-gray-500 text-xs">{item.billing_frequency}</div>
                            </div>
                            <button
                              onClick={() => removeLineItem(index)}
                              className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="flex items-center justify-between mt-2 gap-2">
                            <div className="flex items-center gap-2">
                              <label className="text-xs text-gray-500">Qty:</label>
                              <input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => updateLineItemQuantity(index, parseInt(e.target.value) || 1)}
                                className={`w-14 ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded px-2 py-1 text-sm`}
                              />
                            </div>
                            <div className="flex items-center gap-1">
                              <label className="text-xs text-gray-500">£</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unit_price_gbp}
                                onChange={(e) => updateLineItemPrice(index, parseFloat(e.target.value) || 0)}
                                className={`w-20 ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded px-2 py-1 text-sm text-right`}
                              />
                            </div>
                            <span className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'} font-medium min-w-[70px] text-right`}>
                              £{item.total_gbp?.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ))}
                      
                      {(!proposal.pricing?.line_items || proposal.pricing.line_items.length === 0) && !hasPricingOptions && (
                        <div className={`text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'} text-sm`}>
                          No products added yet
                        </div>
                      )}
                      
                      {/* Add Product Dropdown */}
                      <div className={`mt-3 pt-3 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                        <label className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} mb-2 block`}>Add Product</label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              handleProductSelect(e.target.value);
                              e.target.value = ''; // Reset after selection
                            }
                          }}
                          className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded text-sm p-2 focus:border-[#A2FF00] outline-none`}
                        >
                          <option value="">Select a product...</option>
                          {products.map(product => (
                            <option key={product.product_id} value={product.product_id}>
                              {product.name} - £{product.price_gbp?.toLocaleString()}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Legacy Totals */}
                      {proposal.pricing?.line_items?.length > 0 && (
                        <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} pt-3 mt-3 space-y-2`}>
                          <div className="flex justify-between text-sm">
                            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Subtotal</span>
                            <span className={isDark ? 'text-white' : 'text-gray-900'}>£{(proposal.pricing?.subtotal_gbp || 0).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>VAT (20%)</span>
                            <span className={isDark ? 'text-white' : 'text-gray-900'}>£{(proposal.pricing?.tax_amount_gbp || 0).toLocaleString()}</span>
                          </div>
                          <div className={`flex justify-between text-lg font-bold pt-2 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                            <span className={isDark ? 'text-white' : 'text-gray-900'}>Total</span>
                            <span className={isDark ? 'text-[#A2FF00]' : 'text-green-600'}>£{(proposal.pricing?.total_gbp || 0).toLocaleString()}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* RECIPIENTS TAB */}
              {activeSidebarTab === 'recipients' && (
                <div>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mb-3`}>
                    Select who will receive this proposal. Each gets a unique tracking link.
                  </p>
                  
                  {/* Contact list from card */}
                  <div className="space-y-2 mb-3">
                    {cardContacts.map((contact, idx) => {
                      const contactEmail = contact.email?.toLowerCase();
                      const hasEmail = !!contact.email;
                      const isSelected = hasEmail && selectedRecipients.has(contactEmail);
                      const contactName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unnamed';
                      const roleLabel = contactRoles.find(r => r.value === contact.role)?.label || contact.role;
                      
                      // Find existing recipient to show tracking
                      const existingRecipient = proposal.recipients?.find(r => r.email?.toLowerCase() === contactEmail);
                      
                      return (
                        <div 
                          key={idx}
                          className={`p-2 rounded-sm border transition-colors ${
                            isSelected 
                              ? isDark ? 'border-[#A2FF00]/50 bg-[#A2FF00]/5' : 'border-green-500/50 bg-green-50'
                              : hasEmail 
                                ? isDark ? 'border-white/10 hover:border-white/20' : 'border-gray-200 hover:border-gray-300'
                                : isDark ? 'border-white/5 bg-white/5' : 'border-gray-100 bg-gray-50'
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            {hasEmail ? (
                              <button
                                onClick={() => toggleRecipient(contact.email)}
                                className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                  isSelected 
                                    ? isDark ? 'bg-[#A2FF00] border-[#A2FF00]' : 'bg-green-500 border-green-500'
                                    : isDark ? 'border-white/30 hover:border-[#A2FF00]' : 'border-gray-300 hover:border-green-500'
                                }`}
                              >
                                {isSelected && <Check className="w-3 h-3 text-black" />}
                              </button>
                            ) : (
                              <div className="mt-0.5 w-4 h-4 flex items-center justify-center flex-shrink-0">
                                <AlertCircle className="w-3 h-3 text-yellow-500" />
                              </div>
                            )}
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-white text-sm truncate">{contactName}</span>
                                <span className="text-xs text-gray-500 bg-white/5 px-1.5 py-0.5 rounded">
                                  {roleLabel}
                                </span>
                              </div>
                              {hasEmail ? (
                                <div className="text-xs text-gray-400 truncate">{contact.email}</div>
                              ) : (
                                <button
                                  onClick={() => {
                                    setContactToAddEmail(contact);
                                    setShowAddEmailModal(true);
                                  }}
                                  className="text-xs text-[#A2FF00] hover:underline flex items-center gap-1"
                                >
                                  <Plus className="w-3 h-3" />
                                  Add email
                                </button>
                              )}
                              
                              {/* Show tracking info for existing recipients */}
                              {existingRecipient && isSelected && existingRecipient.tracking?.open_count > 0 && (
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs text-green-400 flex items-center gap-1">
                                    <Eye className="w-3 h-3" />
                                    Opened {existingRecipient.tracking.open_count}x
                                  </span>
                                </div>
                              )}
                            </div>
                            
                            {/* Copy link button for selected recipients */}
                            {existingRecipient && isSelected && (
                              <button
                                onClick={() => copyRecipientLink(existingRecipient)}
                                className="p-1 text-gray-400 hover:text-[#A2FF00] transition-colors"
                                title="Copy unique link"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    
                    {cardContacts.length === 0 && (
                      <div className="text-center py-3 text-gray-500 text-sm">
                        <p>No contacts on this card</p>
                        <p className="text-xs mt-1">Add contacts to the CRM card first</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Add new recipient button - only if there are card contacts without email or to add new */}
                  <button
                    onClick={() => setShowAddRecipientModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white rounded-sm transition-colors text-sm"
                  >
                    <UserPlus className="w-4 h-4" />
                    Add New Contact & Recipient
                  </button>
                  
                  {/* Validation message */}
                  {selectedRecipients.size === 0 && proposal.status === 'draft' && (
                    <div className="mt-3 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded-sm">
                      <p className="text-xs text-yellow-400 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Select at least one recipient from the card contacts above
                      </p>
                    </div>
                  )}
                  
                  {/* Info about recipients */}
                  {cardContacts.filter(c => c.email).length === 0 && cardContacts.length > 0 && (
                    <div className="mt-3 p-2 bg-blue-500/10 border border-blue-500/20 rounded-sm">
                      <p className="text-xs text-blue-400 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Add email addresses to your contacts to send proposals
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* CLIENT TAB */}
              {activeSidebarTab === 'client' && (
                <div className="space-y-3">
                  <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-3`}>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mb-1`}>Company</div>
                    <div className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium`}>{proposal.client?.company_name || 'N/A'}</div>
                  </div>
                  <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-3`}>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mb-1`}>Main Contact</div>
                    <div className={isDark ? 'text-white' : 'text-gray-900'}>
                      {proposal.client?.main_contact_first_name} {proposal.client?.main_contact_last_name}
                    </div>
                    {proposal.client?.main_contact_title && (
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{proposal.client?.main_contact_title}</div>
                    )}
                  </div>
                  <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-3`}>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mb-1`}>Email</div>
                    <div className={`${isDark ? 'text-white' : 'text-gray-900'} text-sm truncate`}>{proposal.client?.main_contact_email || 'N/A'}</div>
                  </div>
                  {proposal.client?.website && (
                    <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-3`}>
                      <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mb-1`}>Website</div>
                      <a 
                        href={proposal.client.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'} text-sm hover:underline truncate block`}
                      >
                        {proposal.client.website}
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* SETTINGS TAB */}
              {activeSidebarTab === 'settings' && (
                <div className="space-y-4">
                  {/* Proposal Status */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">Status</label>
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-medium ${
                      proposal.status === 'signed' ? 'bg-green-500/20 text-green-400' :
                      proposal.status === 'sent' ? 'bg-blue-500/20 text-blue-400' :
                      proposal.status === 'viewed' ? 'bg-purple-500/20 text-purple-400' :
                      proposal.status === 'declined' ? 'bg-red-500/20 text-red-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {proposal.status?.charAt(0).toUpperCase() + proposal.status?.slice(1)}
                    </div>
                  </div>
                  
                  {/* Decline Info in Settings */}
                  {proposal.status === 'declined' && proposal.decline_info && (
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-sm">
                      <p className="text-red-400 text-xs font-medium mb-1">Decline Reason:</p>
                      <p className="text-white text-sm">
                        {
                          proposal.decline_info.reason === 'budget' ? 'Budget constraints' :
                          proposal.decline_info.reason === 'timing' ? 'Timing not right' :
                          proposal.decline_info.reason === 'competitor' ? 'Chose competitor' :
                          proposal.decline_info.reason === 'not_needed' ? 'No longer needed' :
                          proposal.decline_info.reason === 'other' ? 'Other' :
                          proposal.decline_info.reason
                        }
                      </p>
                      {proposal.decline_info.feedback && (
                        <p className="text-gray-400 text-xs mt-2 italic">
                          &ldquo;{proposal.decline_info.feedback}&rdquo;
                        </p>
                      )}
                      <p className="text-gray-500 text-xs mt-2">
                        By: {proposal.decline_info.decliner_name}
                      </p>
                    </div>
                  )}
                  
                  {/* Expiry Date */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">Expires</label>
                    <div className="text-white text-sm">
                      {proposal.expires_at ? new Date(proposal.expires_at).toLocaleDateString() : 'N/A'}
                    </div>
                  </div>
                  
                  {/* Created/Updated */}
                  <div className="pt-3 border-t border-white/10 space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Created</span>
                      <span className="text-gray-400">
                        {proposal.created_at ? new Date(proposal.created_at).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Updated</span>
                      <span className="text-gray-400">
                        {proposal.updated_at ? new Date(proposal.updated_at).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Product with Variants Modal */}
      {showAddProductModal && selectedProduct && (
        <div className={`fixed inset-0 ${isDark ? 'bg-black/80' : 'bg-black/50'} flex items-center justify-center z-50 p-4`}>
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm w-full max-w-lg`}>
            <div className={`p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Add {selectedProduct.name}
              </h2>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-1`}>Select which pricing options to include in this proposal</p>
            </div>
            
            <div className="p-4">
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} mb-4`}>
                The customer will be able to choose between these options in the proposal.
              </p>
              
              <div className="space-y-3">
                {selectedProduct.variants.map((variant, idx) => (
                  <label
                    key={variant.variant_id || idx}
                    className={`flex items-center gap-3 p-3 ${isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'} border rounded cursor-pointer transition-colors`}
                  >
                    <input
                      type="checkbox"
                      defaultChecked={true}
                      id={`variant-${idx}`}
                      className="w-4 h-4"
                    />
                    <div className="flex-1">
                      <div className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium`}>{variant.name}</div>
                      <div className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm`}>{billingLabels[variant.billing_frequency]}</div>
                    </div>
                    <div className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'} font-bold text-lg`}>
                      £{variant.price_gbp.toLocaleString()}
                    </div>
                  </label>
                ))}
              </div>
            </div>
            
            <div className={`p-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'} flex gap-3`}>
              <button
                onClick={() => {
                  setShowAddProductModal(false);
                  setSelectedProduct(null);
                }}
                className={`flex-1 px-4 py-2 ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} rounded-sm transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Get selected variants
                  const selectedVariants = selectedProduct.variants.filter((_, idx) => {
                    const checkbox = document.getElementById(`variant-${idx}`);
                    return checkbox?.checked;
                  });
                  
                  if (selectedVariants.length > 0) {
                    addProductWithVariants(selectedProduct, selectedVariants);
                  } else {
                    alert('Please select at least one variant');
                  }
                }}
                className="flex-1 px-4 py-2 bg-[#A2FF00] hover:bg-[#8EFF00] text-black font-bold rounded-sm transition-colors"
              >
                Add to Proposal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Modal */}
      {showSendModal && (
        <div className={`fixed inset-0 ${isDark ? 'bg-black/80' : 'bg-black/50'} flex items-center justify-center z-50 p-4`}>
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm w-full max-w-lg`}>
            <div className={`p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Send Proposal
              </h2>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Show selected recipients summary */}
              {selectedRecipients.size > 0 && (
                <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-3`}>
                  <h4 className={`text-sm font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} mb-2 flex items-center gap-2`}>
                    <Users className="w-4 h-4" />
                    Recipients ({selectedRecipients.size})
                  </h4>
                  <div className="space-y-1">
                    {Array.from(selectedRecipients).map(email => {
                      const contact = cardContacts.find(c => c.email?.toLowerCase() === email);
                      const name = contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : email;
                      return (
                        <div key={email} className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} flex items-center gap-2`}>
                          <Mail className="w-3 h-3" />
                          <span>{name}</span>
                          <span className={isDark ? 'text-gray-600' : 'text-gray-400'}>({email})</span>
                        </div>
                      );
                    })}
                  </div>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mt-2`}>
                    Each recipient will receive a unique tracking link.
                  </p>
                </div>
              )}
              
              {selectedRecipients.size === 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-sm p-3">
                  <p className="text-sm text-yellow-400 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    No recipients selected. Please select at least one recipient in the sidebar.
                  </p>
                </div>
              )}
              
              <div>
                <label className={`block text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Subject *</label>
                <input
                  type="text"
                  value={sendData.subject}
                  onChange={(e) => setSendData({ ...sendData, subject: e.target.value })}
                  disabled={loadingTemplate}
                  className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-[#A2FF00] outline-none disabled:opacity-50`}
                />
              </div>
              <div>
                <label className={`block text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                  Message
                  {loadingTemplate && <span className={`ml-2 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>(Loading template...)</span>}
                </label>
                <textarea
                  rows={8}
                  value={sendData.message}
                  onChange={(e) => setSendData({ ...sendData, message: e.target.value })}
                  disabled={loadingTemplate}
                  placeholder={loadingTemplate ? 'Loading email template...' : 'Enter your message...'}
                  className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-[#A2FF00] outline-none disabled:opacity-50`}
                />
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mt-1`}>
                  This message is pre-filled from the system email template. Edit as needed before sending.
                </p>
              </div>
            </div>
            
            <div className={`p-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'} flex gap-3`}>
              <button
                onClick={() => setShowSendModal(false)}
                className={`flex-1 px-4 py-2 ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} rounded-sm transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={sendProposal}
                disabled={selectedRecipients.size === 0}
                className="flex-1 px-4 py-2 bg-[#A2FF00] hover:bg-[#8EFF00] text-black font-bold rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Send Proposal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Recipient Modal */}
      {showAddRecipientModal && (
        <div className={`fixed inset-0 ${isDark ? 'bg-black/80' : 'bg-black/50'} flex items-center justify-center z-50 p-4`}>
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm w-full max-w-md`}>
            <div className={`p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'} flex items-center justify-between`}>
              <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Add New Recipient
              </h2>
              <button
                onClick={() => {
                  setShowAddRecipientModal(false);
                  setNewRecipient({ name: '', email: '', role: 'other' });
                }}
                className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                This contact will be added to the card and selected as a recipient.
              </p>
              
              <div>
                <label className={`block text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Name *</label>
                <input
                  type="text"
                  value={newRecipient.name}
                  onChange={(e) => setNewRecipient({ ...newRecipient, name: e.target.value })}
                  placeholder="e.g., John Smith"
                  className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-[#A2FF00] outline-none`}
                />
              </div>
              
              <div>
                <label className={`block text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Email *</label>
                <input
                  type="email"
                  value={newRecipient.email}
                  onChange={(e) => setNewRecipient({ ...newRecipient, email: e.target.value })}
                  placeholder="e.g., john@company.com"
                  className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-[#A2FF00] outline-none`}
                />
              </div>
              
              <div>
                <label className={`block text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Role</label>
                <select
                  value={newRecipient.role}
                  onChange={(e) => setNewRecipient({ ...newRecipient, role: e.target.value })}
                  className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-[#A2FF00] outline-none`}
                >
                  {contactRoles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} mt-1`}>
                  Or type a custom role in the field above
                </p>
              </div>
            </div>
            
            <div className={`p-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'} flex gap-3`}>
              <button
                onClick={() => {
                  setShowAddRecipientModal(false);
                  setNewRecipient({ name: '', email: '', role: 'other' });
                }}
                className={`flex-1 px-4 py-2 ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} rounded-sm transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={addNewRecipient}
                disabled={addingRecipient || !newRecipient.name || !newRecipient.email}
                className="flex-1 px-4 py-2 bg-[#A2FF00] hover:bg-[#8EFF00] text-black font-bold rounded-sm transition-colors disabled:opacity-50"
              >
                {addingRecipient ? 'Adding...' : 'Add Recipient'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Email Modal */}
      {showAddEmailModal && contactToAddEmail && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0A0A0A] border border-white/10 rounded-sm w-full max-w-md">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Add Email Address
              </h2>
              <button
                onClick={() => {
                  setShowAddEmailModal(false);
                  setContactToAddEmail(null);
                  setNewEmail('');
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-400">
                Add email for <span className="text-white font-medium">
                  {contactToAddEmail.first_name} {contactToAddEmail.last_name}
                </span> ({contactToAddEmail.role})
              </p>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Email *</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="e.g., contact@company.com"
                  className="w-full bg-[#111] border border-white/20 rounded-sm text-white px-3 py-2 focus:border-[#A2FF00] outline-none"
                />
              </div>
              
              <p className="text-xs text-gray-500">
                This will update the contact on the card permanently.
              </p>
            </div>
            
            <div className="p-4 border-t border-white/10 flex gap-3">
              <button
                onClick={() => {
                  setShowAddEmailModal(false);
                  setContactToAddEmail(null);
                  setNewEmail('');
                }}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-sm transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={addEmailToContact}
                disabled={!newEmail}
                className="flex-1 px-4 py-2 bg-[#A2FF00] hover:bg-[#8EFF00] text-black font-bold rounded-sm transition-colors disabled:opacity-50"
              >
                Save Email
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default ProposalEditor;
